import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import LayoutContext from 'components/Layout/LayoutContext';
import { getAssembleiasAssessorBffEndpoint } from 'library/Apis';
import { Mutations } from 'library/queries';
import translation from 'locales/translation';
import {
  ButtonThemes,
  CheckCircleIcon,
  Col,
  Modal,
  ModalSizes,
  Row,
  TimesCircleIcon,
  ToastTypes,
} from 'plataforma-fundacao-componentes';
import { FormProvider, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';
import { ModalProps } from 'plataforma-fundacao-componentes/dist/components/modal/Modal';
import { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import {
  AdicionarConvidadoSchema,
  AdicionarConvidadoSchemaType,
} from 'schema/adicionar-convidado.schema';
import ApiService from 'services/ApiService';
import AssociateCheckbox from './fields/associate.field';
import NameField from './fields/name.field';

type MType = {
  nome?: string;
  idConvidado?: string;
  eventId: number;
  onConfirm?: () => Promise<void>;
} & ModalProps;

const language = translation.Assessor.Modal.AdicionarConvidado;

const usePostAddGuest = (
  idEvento: number,
  nome: string,
  idConvidado?: string
) => {
  const url = `${getAssembleiasAssessorBffEndpoint()}convidado`;

  const mutationFn = (data: {
    idEvento: number;
    nome: string;
    isAssociado: boolean;
  }) => {
    if (nome && idConvidado) {
      return ApiService.put(`${url}/${idConvidado}`, {
        eventId: data.idEvento,
        name: data.nome,
        isAssociado: data.isAssociado,
      });
    } else {
      return ApiService.post(`${url}`, {
        eventId: data.idEvento,
        name: data.nome,
        isAssociado: data.isAssociado,
      });
    }
  };

  return useMutation({
    mutationKey: [
      Mutations.PostAdicionarConvidado,
      idEvento,
      nome,
      idConvidado,
    ],
    mutationFn,
    retry: 1,
    retryDelay: 1000 * 2, // 2 seconds
    gcTime: Infinity,
  });
};

export const AdicionarConvidado = ({
  nome,
  eventId,
  idConvidado,
  ...rest
}: MType) => {
  const [close, setClose] = useState<boolean>(false);

  const { showToast } = useContext(LayoutContext);

  const methods = useForm<AdicionarConvidadoSchemaType>({
    resolver: zodResolver(AdicionarConvidadoSchema),
    defaultValues: {
      isAssociado: false,
    },
  });

  const { isPending, mutate } = usePostAddGuest(
    eventId,
    methods.watch('name'),
    idConvidado
  );

  const sendData = useCallback(
    (close: boolean) => {
      return () =>
        mutate(
          {
            idEvento: eventId,
            nome: methods.watch('name'),
            isAssociado: methods.watch('isAssociado') ?? false,
          },
          {
            onSuccess: () => {
              methods.setValue('name', '');
              methods.setValue('isAssociado', false);

              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Success,
                showStatusBar: true,
                timeout: 4000,
                label:
                  nome && idConvidado
                    ? 'Convidado editado com sucesso!'
                    : 'Convidado adicionado com sucesso!',
              });

              if (nome && idConvidado) rest.onConfirm?.();
              if (close) rest.onClose?.();
            },
            onError: (error: Error) => {
              showToast({
                pauseOnFocusLoss: true,
                theme: ToastTypes.Error,
                showStatusBar: true,
                timeout: 4000,
                label:
                  translation.Toasts.Errors.Keys[
                    `${
                      (error as AxiosError<Error>).response?.data.message ||
                      (error as AxiosError<Error>).response?.data ||
                      (error as AxiosError<Error>).message
                    }` as keyof typeof translation.Toasts.Errors.Keys
                  ] ?? translation.Toasts.Errors.defaultAlternative,
              });
            },
          }
        );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventId, idConvidado, methods, mutate, nome, rest, showToast, close]
  );

  useEffect(() => {
    if (nome && idConvidado) {
      methods.setValue('name', nome);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      title={nome ? language.titleEdit : language.title}
      preventEscExit={rest.preventEscExit || false}
      preventMaskExit={rest.preventMaskExit || false}
      modalKey={rest.modalKey}
      mobileOn={rest.mobileOn}
      onClose={rest.onClose}
      size={ModalSizes.Large}
      footer={
        nome
          ? [
              {
                theme: ButtonThemes.Secondary,
                leftIcon: <TimesCircleIcon />,
                children: language.cancelar,
                disabled: isPending,
                onClick: () => {
                  methods.reset(undefined, { keepDefaultValues: true });
                  rest.onClose?.();
                },
              },
              {
                theme: ButtonThemes.Primary,
                leftIcon: <CheckCircleIcon />,
                children: language.editar,
                disabled:
                  isPending ||
                  !!methods.formState.errors.name ||
                  methods.watch('name') === nome,
                type: 'submit',
                form: 'hook-form',
                onClick: () => {
                  setClose(true);
                },
                loading: isPending,
              },
            ]
          : [
              {
                theme: ButtonThemes.Secondary,
                leftIcon: <TimesCircleIcon />,
                children: language.cancelar,
                disabled: isPending,
                onClick: () => {
                  methods.reset(undefined, { keepDefaultValues: true });
                  rest.onClose?.();
                },
              },
              {
                theme: ButtonThemes.Primary,
                leftIcon: <CheckCircleIcon />,
                children: language.adicionar,
                disabled: isPending || !!methods.formState.errors.name,
                type: 'submit',
                form: 'hook-form',
                onClick: () => {
                  setClose(false);
                },
                loading: isPending,
              },
              {
                theme: ButtonThemes.Primary,
                leftIcon: <CheckCircleIcon />,
                children: language.adicionarSair,
                disabled: isPending || !!methods.formState.errors.name,
                type: 'submit',
                form: 'hook-form',
                onClick: () => {
                  setClose(true);
                },
                loading: isPending,
              },
            ]
      }
    >
      <FormProvider {...methods}>
        <form id={'hook-form'} onSubmit={methods.handleSubmit(sendData(close))}>
          <Row>
            <Col>
              <NameField />
            </Col>
            {!nome ? (
              <Col>
                <AssociateCheckbox />
              </Col>
            ) : (
              <Fragment />
            )}
          </Row>
        </form>
      </FormProvider>
    </Modal>
  );
};
